import type { Room, Booking, BookingStatus } from '@/types/booking';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StickyNote, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';
import { Checkbox } from '@/components/ui/checkbox';
import { TimeSelect } from '@/components/ui/time-select';
import { useBookings } from '@/contexts/BookingContext';
import { validateBookingTime } from '@/lib/booking-utils';
import { useToast } from '@/hooks/use-toast';
import { ConflictWarningDialog } from './ConflictWarningDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type BatchEditBookingDialogProps = {
  bookings: Booking[];
  onClose: () => void;
  onUpdate: (updates: Partial<Booking>) => void;
  onDelete: (bookingIds: number[]) => void;
};

const statusStyles = {
  paid: 'px-3 py-1.5 w-[200px] bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 transition-all duration-200',
  booked: 'px-3 py-1.5 w-[200px] bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 transition-all duration-200',
  pending: 'px-3 py-1.5 w-[200px] bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 transition-all duration-200'
};

const rooms: Room[] = [
  { id: 1, name: 'Main Room', capacity: 350, color: 'bg-orange-50 border-l-4 border-orange-500' },
  { id: 2, name: 'Meeting Room', capacity: 20, color: 'bg-emerald-50 border-l-4 border-emerald-500' },
  { id: 3, name: 'Meeting Room Main Left', capacity: 100, color: 'bg-blue-50 border-l-4 border-blue-500' },
  { id: 4, name: 'Meeting Room Main Right', capacity: 50, color: 'bg-purple-50 border-l-4 border-purple-500' },
  { id: 5, name: 'Multiuse Room Small', capacity: 15, color: 'bg-pink-50 border-l-4 border-pink-500' },
  { id: 6, name: 'Multiuse Room Large', capacity: 30, color: 'bg-cyan-50 border-l-4 border-cyan-500' },
  { id: 7, name: 'Studio Music', capacity: 8, color: 'bg-indigo-50 border-l-4 border-indigo-500' },
  { id: 8, name: 'Studio Podcast', capacity: 8, color: 'bg-rose-50 border-l-4 border-rose-500' }
];

export function BatchEditBookingDialog({
  bookings,
  onClose,
  onUpdate,
  onDelete
}: BatchEditBookingDialogProps) {
  const { checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);
  const [fieldsToUpdate, setFieldsToUpdate] = useState({
    status: false,
    amount: false,
    notes: false,
    timeSlot: false,
    roomId: false,
    duration: false
  });

  const [form, setForm] = useState({
    status: 'pending' as BookingStatus,
    amount: 0,
    hasNotes: false,
    notes: '',
    timeSlot: '09:00',
    roomId: rooms[0].id,
    duration: '1',
    isFullDay: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const updates: Partial<Booking> = {};
    
    if (fieldsToUpdate.status) {
      updates.status = form.status;
    }
    
    if (fieldsToUpdate.timeSlot) {
      updates.timeSlot = form.isFullDay ? '09:00' : form.timeSlot;
    }

    if (fieldsToUpdate.roomId) {
      updates.roomId = form.roomId;
    }

    if (fieldsToUpdate.duration) {
      updates.duration = form.isFullDay ? 14 : parseInt(form.duration);
    }
    
    if (fieldsToUpdate.amount || fieldsToUpdate.notes) {
      updates.revenue = {
        amount: fieldsToUpdate.amount ? form.amount : bookings[0].revenue?.amount || 0,
        hasNotes: fieldsToUpdate.notes ? form.hasNotes : bookings[0].revenue?.hasNotes || false,
        notes: fieldsToUpdate.notes ? (form.hasNotes ? form.notes : undefined) : bookings[0].revenue?.notes
      };
    }

    // Check for conflicts if time/room/duration is being updated
    if (fieldsToUpdate.timeSlot || fieldsToUpdate.roomId || fieldsToUpdate.duration) {
      for (const booking of bookings) {
        const updatedBooking = {
          ...booking,
          ...updates
        };

        // Validate booking time
        const timeError = validateBookingTime(updatedBooking);
        if (timeError) {
          toast({
            title: "Invalid Time",
            description: timeError,
            variant: "destructive"
          });
          return;
        }

        // Check for conflicts
        const conflict = checkDuplicate(updatedBooking, booking.id);
        if (conflict) {
          setConflictBooking(updatedBooking);
          setShowConflictWarning(true);
          return;
        }
      }
    }

    onUpdate(updates);
    onClose();
  };

  const handleDurationChange = (value: string) => {
    if (value === 'full') {
      setForm(prev => ({ 
        ...prev, 
        duration: '14', 
        isFullDay: true,
        timeSlot: '09:00' // Always set to 9:00 for full day bookings
      }));
    } else {
      setForm(prev => ({ 
        ...prev, 
        duration: value, 
        isFullDay: false 
      }));
    }
  };

  const handleDelete = () => {
    onDelete(bookings.map(b => b.id));
    onClose();
  };

  const handleAlternativeSelect = (newDate: string, newTime: string) => {
    if (conflictBooking) {
      const updates: Partial<Booking> = {
        timeSlot: newTime
      };
      
      onUpdate(updates);
      onClose();
    }
  };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Batch Edit {bookings.length} Bookings</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Time Slot */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-time"
                  checked={fieldsToUpdate.timeSlot}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, timeSlot: checked as boolean }))
                  }
                />
                <Label htmlFor="update-time">Update Start Time</Label>
              </div>

              {fieldsToUpdate.timeSlot && (
                <div className="space-y-2 ml-6">
                  <TimeSelect
                    value={form.timeSlot}
                    onChange={(value) => setForm(prev => ({ ...prev, timeSlot: value }))}
                    disabled={form.isFullDay}
                  />
                </div>
              )}

              {/* Room Selection */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-room"
                  checked={fieldsToUpdate.roomId}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, roomId: checked as boolean }))
                  }
                />
                <Label htmlFor="update-room">Update Location</Label>
              </div>

              {fieldsToUpdate.roomId && (
                <div className="space-y-2 ml-6">
                  <Select
                    value={form.roomId.toString()}
                    onValueChange={(value) => setForm(prev => ({ ...prev, roomId: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(room => (
                        <SelectItem key={room.id} value={room.id.toString()}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Duration */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-duration"
                  checked={fieldsToUpdate.duration}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, duration: checked as boolean }))
                  }
                />
                <Label htmlFor="update-duration">Update Duration</Label>
              </div>

              {fieldsToUpdate.duration && (
                <div className="space-y-2 ml-6">
                  <Select 
                    value={form.isFullDay ? 'full' : form.duration}
                    onValueChange={handleDurationChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full" className="font-medium text-purple-600">
                        Full Day (9:00-23:00)
                      </SelectItem>
                      <SelectItem value="1">1 hour</SelectItem>
                      {Array.from({ length: 7 }, (_, i) => (
                        <SelectItem key={i + 2} value={(i + 2).toString()}>
                          {i + 2} hours
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-status"
                  checked={fieldsToUpdate.status}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, status: checked as boolean }))
                  }
                />
                <Label htmlFor="update-status">Update Status</Label>
              </div>

              {fieldsToUpdate.status && (
                <div className="space-y-2 ml-6">
                  <Select 
                    value={form.status}
                    onValueChange={(value) => setForm({...form, status: value as BookingStatus})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">
                        <div className={cn("px-3 py-1 rounded-md", statusStyles.paid)}>
                          Paid
                        </div>
                      </SelectItem>
                      <SelectItem value="booked">
                        <div className={cn("px-3 py-1 rounded-md", statusStyles.booked)}>
                          Booked
                        </div>
                      </SelectItem>
                      <SelectItem value="pending">
                        <div className={cn("px-3 py-1 rounded-md", statusStyles.pending)}>
                          Pending
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Amount */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-amount"
                  checked={fieldsToUpdate.amount}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, amount: checked as boolean }))
                  }
                />
                <Label htmlFor="update-amount">Update Amount</Label>
              </div>

              {fieldsToUpdate.amount && (
                <div className="space-y-2 ml-6">
                  <Label htmlFor="amount">Amount (€)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      €
                    </span>
                    <Input
                      id="amount"
                      type="text"
                      inputMode="decimal"
                      pattern="^\d*\.?\d{0,2}$"
                      className="pl-7"
                      value={form.amount.toString()}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = value.split('.');
                        if (parts.length > 2) return;
                        if (parts[1]?.length > 2) return;
                        
                        setForm(prev => ({
                          ...prev,
                          amount: value === '' ? 0 : parseFloat(value) || 0
                        }));
                      }}
                      style={{ appearance: 'textfield' }}
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="update-notes"
                  checked={fieldsToUpdate.notes}
                  onCheckedChange={(checked) => 
                    setFieldsToUpdate(prev => ({ ...prev, notes: checked as boolean }))
                  }
                />
                <Label htmlFor="update-notes">Update Notes</Label>
              </div>

              {fieldsToUpdate.notes && (
                <div className="space-y-4 ml-6">
                  <ToggleWithLabel
                    id="notes-enabled"
                    checked={form.hasNotes}
                    onChange={(checked) => 
                      setForm(prev => ({
                        ...prev,
                        hasNotes: checked
                      }))
                    }
                    label="Add Notes"
                    leftIcon={<StickyNote className="h-4 w-4" />}
                  />

                  {form.hasNotes && (
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        placeholder="Add any notes..."
                        value={form.notes}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Delete {bookings.length} Bookings
              </Button>

              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!Object.values(fieldsToUpdate).some(Boolean)}
              >
                Update {bookings.length} Bookings
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking)!}
          rooms={[rooms.find(r => r.id === conflictBooking.roomId)!]}
          open={showConflictWarning}
          onClose={() => setShowConflictWarning(false)}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {bookings.length} Bookings
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete {bookings.length} Bookings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}